/**
 * Account Settings → Notifications (#571 PR 5, role-scoped via #638).
 *
 * Per-event × per-transport preference matrix. Backend filters the
 * event list to events the user is in-scope for (#638 — role-scoping)
 * so a tester user doesn't see `agent.budget_tripped`'s row, etc.
 * Labels + descriptions also come from the backend metadata — the
 * UI no longer hardcodes either, so a copy change is a one-file
 * backend edit.
 *
 * Save flow mirrors the other Settings tabs (Profile / General):
 * local edits stage into form state, "Save changes" PUTs only the
 * entries the user actually touched, "Reset" reverts to the last-
 * saved baseline. No per-toggle optimistic save — the form-level
 * save matches the rest of Settings for consistency.
 *
 * When this installation can't send email, the Email column and the
 * PDF picker are hidden and the header says why. Saved email choices are
 * left as they are, so they apply again if email is turned on later.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Bell, AlertCircle, ShieldCheck } from 'lucide-react'
import { Badge, Button, Checkbox } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useEmailStatus } from '@/hooks/usePublicSettings'
import {
  notificationsAPI,
  type NotificationEventType,
  type NotificationPreferenceEvent,
  type NotificationRoleContext,
  type NotificationScope,
  type NotificationTransportPrefs,
} from '@/lib/api'

export function AccountNotifications() {
  const [events, setEvents] = useState<NotificationPreferenceEvent[] | null>(null)
  /** Last-saved baseline. Drives the dirty check and the Reset button.
   * Held separately from `events` so a fresh load and a successful
   * save both update it in lockstep, but local edits don't. */
  const [savedEvents, setSavedEvents] = useState<NotificationPreferenceEvent[] | null>(null)
  /** The viewer's resolved roles, backing the role-context strip
   * (#1289 Slice 2). Loaded once with the events; preference writes
   * don't change it, so it isn't re-fetched on save. */
  const [roleContext, setRoleContext] = useState<NotificationRoleContext | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const emailOff = useEmailStatus() === 'off'
  // Both class strings are written out whole so the CSS build sees them.
  const columns = emailOff ? 'grid-cols-[1fr_80px]' : 'grid-cols-[1fr_80px_80px]'

  useEffect(() => {
    let cancelled = false
    void notificationsAPI.getPreferences()
      .then((data) => {
        if (cancelled) return
        setEvents(data.events)
        setSavedEvents(data.events)
        setRoleContext(data.roleContext)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err))
      })
    return () => { cancelled = true }
  }, [])

  /** Set of event types whose prefs differ from the saved baseline.
   * Computed once per render off the two arrays. Drives the form-
   * level dirty flag (which gates Save + Reset) and the per-event
   * PUT-payload filter on save. */
  const dirtyTypes = useMemo(() => {
    if (!events || !savedEvents) return new Set<NotificationEventType>()
    const savedByType = new Map(savedEvents.map((e) => [e.type, e.prefs]))
    const out = new Set<NotificationEventType>()
    for (const e of events) {
      const prior = savedByType.get(e.type)
      if (
        !prior ||
        prior.inApp !== e.prefs.inApp ||
        prior.email !== e.prefs.email ||
        !shallowEqualSettings(prior.settings, e.prefs.settings)
      ) {
        out.add(e.type)
      }
    }
    return out
  }, [events, savedEvents])

  const dirty = dirtyTypes.size > 0

  const toggle = (
    eventType: NotificationEventType,
    transport: 'inApp' | 'email',
    next: boolean,
  ) => {
    setEvents((prev) =>
      prev?.map((e) =>
        e.type === eventType ? { ...e, prefs: { ...e.prefs, [transport]: next } } : e,
      ) ?? null,
    )
    setSaveError(null)
    setSavedAt(null)
  }

  /** Merge per-event settings (e.g. `run.completed`'s pdfType
   * picker). Stages into the same `events` form state the
   * inApp/email toggles use, so the dirty-check picks it up the
   * same way and the save flow PUTs the merged shape. */
  const updateSettings = (
    eventType: NotificationEventType,
    patch: Record<string, string>,
  ) => {
    setEvents((prev) =>
      prev?.map((e) =>
        e.type === eventType
          ? {
              ...e,
              prefs: {
                ...e.prefs,
                settings: { ...(e.prefs.settings ?? {}), ...patch },
              },
            }
          : e,
      ) ?? null,
    )
    setSaveError(null)
    setSavedAt(null)
  }

  const handleSave = async () => {
    if (!events || !savedEvents || !dirty) return
    setSubmitting(true)
    setSaveError(null)
    try {
      const patch: Partial<Record<NotificationEventType, NotificationTransportPrefs>> = {}
      for (const e of events) {
        if (dirtyTypes.has(e.type)) patch[e.type] = e.prefs
      }
      const updated = await notificationsAPI.setPreferences(patch)
      setEvents(updated)
      setSavedEvents(updated)
      setSavedAt(Date.now())
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleReset = () => {
    if (!savedEvents) return
    setEvents(savedEvents)
    setSaveError(null)
    setSavedAt(null)
  }

  if (loadError) {
    return (
      <div className="space-y-4 max-w-2xl">
        <Header emailOff={emailOff} />
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>Failed to load notification preferences: {loadError}</span>
        </div>
      </div>
    )
  }

  if (!events) {
    return (
      <div className="space-y-4 max-w-2xl">
        <Header emailOff={emailOff} />
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    )
  }

  if (events.length === 0) {
    // User is in-scope for no events at all — unusual but possible
    // for a brand-new user with no org membership / no superadmin
    // flag. Show a friendly empty state instead of a blank table.
    return (
      <div className="space-y-4 max-w-2xl">
        <Header emailOff={emailOff} />
        <div className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
          No notification preferences to manage yet — you'll see options here
          once you join an organization or workspace.
        </div>
      </div>
    )
  }

  // Fence the Global-Admin-only events (the superadmin-scoped ones)
  // into their own section, away from the tenant-scoped groups. The
  // predicate is the event's scope, NOT its `group` field: the
  // `agent.budget_tripped` event lives in the `Agents` group but is
  // superadmin-scoped, so it belongs in the fenced section alongside
  // the `Platform`-group events (#1289 Slice 2 — matches the mockup).
  const isPlatform = (evt: NotificationPreferenceEvent) => evt.scope.kind === 'superadmin'
  const tenantEvents = events.filter((e) => !isPlatform(e))
  const platformEvents = events.filter(isPlatform)

  // Tenant events keep their per-`group` sections, in first-seen order.
  const tenantGroups = new Map<string, NotificationPreferenceEvent[]>()
  for (const evt of tenantEvents) {
    const list = tenantGroups.get(evt.group) ?? []
    list.push(evt)
    tenantGroups.set(evt.group, list)
  }

  /** One event row — shared between the tenant matrix and the fenced
   * Platform section so the two render identically. */
  const renderRow = (evt: NotificationPreferenceEvent) => (
    <div
      key={evt.type}
      className={cn('grid items-start gap-x-4 px-4 py-3 border-b last:border-b-0', columns)}
    >
      <div>
        <div className="text-sm font-medium">{evt.label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{evt.description}</div>
        {/* Per-event settings UI — only rendered when the event has
            settings to show AND the email transport is on (settings
            are email-only today). Currently just `run.completed`'s
            PDF-type picker; future events register their own renderer
            here as the registry grows. */}
        {evt.type === 'run.completed' && evt.prefs.email && !emailOff && (
          <RunCompletedSettings
            value={evt.prefs.settings?.pdfType ?? 'summary'}
            onChange={(v) => updateSettings(evt.type, { pdfType: v })}
            disabled={submitting}
          />
        )}
      </div>
      <div className="flex justify-center pt-0.5">
        <Checkbox
          checked={evt.prefs.inApp}
          onCheckedChange={(v) => toggle(evt.type, 'inApp', v)}
          disabled={submitting}
          aria-label={`${evt.label} — in-app notifications`}
        />
      </div>
      {!emailOff && (
        <div className="flex justify-center pt-0.5">
          <Checkbox
            checked={evt.prefs.email}
            onCheckedChange={(v) => toggle(evt.type, 'email', v)}
            disabled={submitting}
            aria-label={`${evt.label} — email notifications`}
          />
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-6 max-w-2xl">
      <Header emailOff={emailOff} />

      {roleContext && <RoleContextStrip ctx={roleContext} />}

      {saveError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm flex items-start gap-2" role="alert">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>Couldn't save preferences: {saveError}</span>
        </div>
      )}

      {tenantGroups.size > 0 && (
        <div className="rounded-md border border-border overflow-hidden">
          <div className={cn('grid items-center gap-x-4 px-4 py-2 text-xs font-semibold text-muted-foreground bg-muted/40 border-b', columns)}>
            <div>Event</div>
            <div className="text-center">In-app</div>
            {!emailOff && <div className="text-center">Email</div>}
          </div>
          {Array.from(tenantGroups.entries()).map(([groupName, groupEvents]) => {
            const badge = groupScopeBadge(groupEvents[0].scope)
            return (
              <div key={groupName}>
                <div className="flex items-center gap-2 px-4 py-1.5 bg-muted/20 border-b">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {groupName}
                  </span>
                  <Pill tier={badge.tier}>{badge.text}</Pill>
                </div>
                {groupEvents.map(renderRow)}
              </div>
            )
          })}
        </div>
      )}

      {/* Global-Admin-only section — fenced apart on a distinct trust
          boundary. Shown only when the viewer is in scope for at least
          one superadmin event (i.e. they're a Global Admin), so a
          tester / org member never sees it. */}
      {platformEvents.length > 0 && (
        <div
          data-testid="platform-group"
          className="rounded-md border border-cyan-500/30 overflow-hidden"
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border-b border-cyan-500/30">
            <ShieldCheck className="h-4 w-4 text-cyan-600 dark:text-cyan-400 shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">
              Platform · Global Admin
            </span>
          </div>
          <p className="px-4 py-2 text-xs text-cyan-700 dark:text-cyan-300 bg-cyan-500/5 border-b border-cyan-500/20">
            Only Global Admins see these. They're alerts about this
            installation's own health, not about your tests.
          </p>
          {platformEvents.map(renderRow)}
        </div>
      )}

      {savedAt && !dirty && !saveError && (
        <p className="text-sm text-success" key={savedAt}>Saved.</p>
      )}

      <div className="flex gap-2">
        <Button onClick={() => void handleSave()} disabled={!dirty || submitting}>
          {submitting ? 'Saving…' : 'Save changes'}
        </Button>
        <Button type="button" variant="ghost" onClick={handleReset} disabled={!dirty || submitting}>
          Reset
        </Button>
      </div>
    </div>
  )
}

function Header({ emailOff }: { emailOff: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <Bell className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
      <div>
        <h3 className="text-base font-semibold">Notifications</h3>
        <p className="text-sm text-muted-foreground">
          {emailOff
            ? "Choose which events appear in your bell-icon feed. This installation can't send email, so email notifications are not offered."
            : 'Choose which events appear in your bell-icon feed and which arrive by email.'}
        </p>
      </div>
    </div>
  )
}

/** Color treatments per role tier — mirrors the mockup's pill palette
 * (workspace blue, org purple, platform cyan, creator amber). */
type RoleTier = 'workspace' | 'org' | 'platform' | 'creator' | 'team'
const PILL_CLASS: Record<RoleTier, string> = {
  workspace: 'border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  org: 'border-purple-500/20 bg-purple-500/10 text-purple-600 dark:text-purple-400',
  platform: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
  creator: 'border-amber-500/20 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  team: 'border-border bg-muted text-muted-foreground',
}

/** Plain-language labels for the roles a viewer holds — never the raw
 * enum, never "superadmin". The backend ships the enum value; the
 * customer-facing wording lives here so it's edited in one place. */
const WORKSPACE_ROLE_LABEL: Record<string, string> = {
  admin: 'Workspace Admin',
  tester: 'Workspace Tester',
  reviewer: 'Workspace Reviewer',
}
const ORG_ROLE_LABEL: Record<string, string> = {
  owner: 'Org Owner',
  admin: 'Org Admin',
  billing: 'Org Billing',
  member: 'Org Member',
}

/** Short, tier-less role labels for the compact per-group badges
 * (e.g. "Org · Owner + Admin"). The tier is already named by the
 * badge prefix, so these omit it — but they still map the raw enum to
 * a capitalized word rather than leaking the literal (plain-language
 * rule). */
const WORKSPACE_ROLE_SHORT: Record<string, string> = {
  admin: 'Admin',
  tester: 'Tester',
  reviewer: 'Reviewer',
}
const ORG_ROLE_SHORT: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  billing: 'Billing',
  member: 'Member',
}

function Pill({
  tier,
  children,
}: {
  tier: RoleTier
  children: ReactNode
}) {
  return (
    <Badge variant="outline" className={cn('font-medium', PILL_CLASS[tier])}>
      {children}
    </Badge>
  )
}

/**
 * The role-context strip (#1289 Slice 2). Names exactly the roles the
 * viewer holds — each workspace + role, each org + role, and the
 * Global Admin badge when applicable — so the operator can see why
 * this set of events appears. Renders nothing when the viewer holds
 * no surfaced roles (a brand-new user), avoiding an empty banner.
 */
function RoleContextStrip({ ctx }: { ctx: NotificationRoleContext }) {
  const hasAny =
    ctx.workspaces.length > 0 || ctx.orgs.length > 0 || ctx.isGlobalAdmin
  if (!hasAny) return null
  return (
    <div
      data-testid="role-context-strip"
      className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-sm"
    >
      <span className="text-muted-foreground mr-2">
        You're seeing these options because you're:
      </span>
      <span className="inline-flex flex-wrap gap-1.5 align-middle">
        {ctx.workspaces.map((w, i) => (
          <Pill key={`ws-${i}`} tier="workspace">
            {WORKSPACE_ROLE_LABEL[w.role] ?? w.role} · {w.name}
          </Pill>
        ))}
        {ctx.orgs.map((o, i) => (
          <Pill key={`org-${i}`} tier="org">
            {ORG_ROLE_LABEL[o.role] ?? o.role} · {o.name}
          </Pill>
        ))}
        {ctx.isGlobalAdmin && <Pill tier="platform">Global Admin</Pill>}
      </span>
    </div>
  )
}

/** Derive a per-group badge from the recipient scope of that group's
 * events (every event in a tenant group shares one scope). Describes
 * WHO sees the group in plain language — "Workspace · all roles",
 * "Org · owner + admin", "Creator & team" — so the operator can read
 * the role behind each section. */
function groupScopeBadge(scope: NotificationScope): { tier: RoleTier; text: string } {
  switch (scope.kind) {
    case 'superadmin':
      return { tier: 'platform', text: 'Global Admin' }
    case 'workspace':
      return {
        tier: 'workspace',
        text:
          scope.roles.length >= 3
            ? 'Workspace · all roles'
            : `Workspace · ${scope.roles.map((r) => WORKSPACE_ROLE_SHORT[r] ?? r).join(' + ')}`,
      }
    case 'org':
      return {
        tier: 'org',
        text: `Org · ${scope.roles.map((r) => ORG_ROLE_SHORT[r] ?? r).join(' + ')}`,
      }
    case 'creatorAndTeam':
      return { tier: 'creator', text: 'Creator & team' }
    case 'team':
      return { tier: 'team', text: 'Team' }
  }
}

/**
 * Order-insensitive comparison for two settings maps. Treats
 * `undefined`, `null`, and empty object as equivalent (the save
 * path drops empty `settings` objects to `undefined`). Used by
 * the dirty check so a future settings shape with multiple keys
 * doesn't false-positive based on JSON serialization order.
 */
function shallowEqualSettings(
  a: Record<string, string> | undefined,
  b: Record<string, string> | undefined,
): boolean {
  const aKeys = a ? Object.keys(a) : []
  const bKeys = b ? Object.keys(b) : []
  if (aKeys.length !== bKeys.length) return false
  for (const k of aKeys) {
    if (
      (a as Record<string, string>)[k] !==
      (b as Record<string, string> | undefined)?.[k]
    ) {
      return false
    }
  }
  return true
}

/**
 * PDF-type picker for the `run.completed` event. Rendered inline
 * under the event row (left column) when the user has email
 * notifications ON for that event — the picker is meaningless
 * for in-app-only opt-ins (the in-app bell doesn't carry the
 * PDF).
 *
 * Two options:
 *   - "Summary" — single-page run-detail PDF. Smaller, faster.
 *   - "Detailed" — multi-page per-test breakdown with steps +
 *     screenshots. Replay-only on the backend (discovery runs
 *     fall back to Summary at attachment-build time).
 */
function RunCompletedSettings({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (next: string) => void
  disabled?: boolean
}) {
  return (
    <div className="mt-2 flex items-center gap-2 text-xs">
      <label
        htmlFor="run-completed-pdf-type"
        className="text-muted-foreground"
      >
        PDF attached:
      </label>
      <select
        id="run-completed-pdf-type"
        value={value === 'detailed' ? 'detailed' : 'summary'}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-7 rounded-md border border-input bg-background px-2 text-xs"
      >
        <option value="summary">Summary (1 page, faster)</option>
        <option value="detailed">Detailed (per-test, slower)</option>
      </select>
    </div>
  )
}
